import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { searchContacts } from '@/features/campanhas/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, RotateCcw, ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import {
  type LgpdRequestRow,
  createLgpdRequest,
  downloadLgpdResult,
  listLgpdRequests,
  retryLgpdRequest,
} from '../api';

const STATUS_LABELS: Record<LgpdRequestRow['status'], string> = {
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluído',
  failed: 'Falha',
};

const STATUS_COLORS: Record<LgpdRequestRow['status'], string> = {
  pending: 'bg-n-100 text-n-700',
  processing: 'bg-brand-100 text-brand-700',
  completed: 'bg-success-100 text-success-700',
  failed: 'bg-danger-100 text-danger-700',
};

const TYPE_LABELS: Record<LgpdRequestRow['request_type'], string> = {
  export: 'Exportar dados',
  delete: 'Apagar',
  anonymize: 'Anonimizar',
};

export function LgpdTab() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const listQ = useQuery({
    queryKey: ['lgpd-requests'],
    queryFn: listLgpdRequests,
    refetchInterval: 5000,
  });

  const downloadMut = useMutation({
    mutationFn: (id: number) => downloadLgpdResult(id),
    onSuccess: (res) => window.open(res.url, '_blank', 'noopener'),
    onError: (err) => {
      if (err instanceof ApiError) alert(err.problem.detail ?? err.problem.title);
    },
  });
  const retryMut = useMutation({
    mutationFn: (id: number) => retryLgpdRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lgpd-requests'] }),
  });

  return (
    <section>
      <header className="mb-5 flex items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Conformidade LGPD
          </h2>
          <p className="text-[13px] text-n-500">
            Gerencie pedidos de exportação, exclusão e anonimização de dados de titulares.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <ShieldAlert aria-hidden="true" />
          Novo pedido
        </Button>
      </header>

      <div className="overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                #
              </th>
              <th className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                Tipo
              </th>
              <th className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                Contato
              </th>
              <th className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                Status
              </th>
              <th className="whitespace-nowrap px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                Concluído em
              </th>
              <th className="whitespace-nowrap px-[14px] py-[10px] text-right font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!listQ.isLoading && (listQ.data ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-n-500">
                  Nenhum pedido LGPD ainda.
                </td>
              </tr>
            )}
            {(listQ.data ?? []).map((r) => (
              <tr key={r.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] text-n-700">#{r.id}</td>
                <td className="px-[14px] py-[11px]">
                  <span
                    className={`rounded-pill px-2 py-[2px] font-bold text-[10px] uppercase ${
                      r.request_type === 'delete'
                        ? 'bg-danger-50 text-danger-700'
                        : r.request_type === 'anonymize'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-brand-50 text-brand-700'
                    }`}
                  >
                    {TYPE_LABELS[r.request_type]}
                  </span>
                </td>
                <td className="px-[14px] py-[11px] text-n-700">
                  {r.contact_snapshot?.display_name ?? `#${r.contact_id ?? '?'}`}
                  {r.contact_snapshot?.phone && (
                    <span className="ml-2 font-mono text-[11px] text-n-500">
                      {r.contact_snapshot.phone}
                    </span>
                  )}
                </td>
                <td className="px-[14px] py-[11px]">
                  <span
                    className={`rounded-pill px-2 py-[2px] font-bold text-[10px] uppercase ${STATUS_COLORS[r.status]}`}
                  >
                    {STATUS_LABELS[r.status]}
                  </span>
                  {r.error_message && (
                    <span className="ml-2 text-[10px] text-danger-600">{r.error_message}</span>
                  )}
                </td>
                <td className="px-[14px] py-[11px] text-n-600">
                  {r.completed_at ? new Date(r.completed_at).toLocaleString('pt-BR') : '—'}
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === 'completed' && r.has_result && (
                      <button
                        type="button"
                        onClick={() => downloadMut.mutate(r.id)}
                        className="flex items-center gap-1 rounded border border-n-200 bg-n-0 px-2 py-1 text-[11px] font-semibold hover:bg-n-50"
                        title="Baixar ZIP"
                      >
                        <Download size={12} />
                        Baixar
                      </button>
                    )}
                    {r.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => retryMut.mutate(r.id)}
                        className="flex items-center gap-1 rounded border border-n-200 bg-n-0 px-2 py-1 text-[11px] font-semibold hover:bg-n-50"
                        title="Reprocessar"
                      >
                        <RotateCcw size={12} />
                        Reprocessar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewLgpdRequestModal onClose={() => setCreating(false)} />}
    </section>
  );
}

function NewLgpdRequestModal({ onClose }: { onClose: () => void }) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [contactId, setContactId] = useState<number | null>(null);
  const [contactName, setContactName] = useState<string>('');
  const [requestType, setRequestType] = useState<LgpdRequestRow['request_type']>('export');
  const [error, setError] = useState<string | null>(null);

  const contactsQ = useQuery({
    queryKey: ['contacts-search', search],
    queryFn: () => searchContacts(search),
    enabled: search.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () =>
      createLgpdRequest({ contact_id: contactId!, request_type: requestType }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lgpd-requests'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 shadow-xl">
        <header className="flex items-center justify-between border-n-200 border-b px-6 py-4">
          <h3 className="font-bold text-[16px] text-n-900">Novo pedido LGPD</h3>
          <button type="button" onClick={onClose} className="text-n-500 hover:text-n-700">
            <X size={18} />
          </button>
        </header>

        <div className="space-y-4 px-6 py-5 text-[13px]">
          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">
              Buscar contato
            </span>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome do titular…"
            />
            {contactsQ.data && contactsQ.data.contacts.length > 0 && (
              <div className="mt-2 max-h-40 overflow-auto rounded border border-n-200 bg-n-0">
                {contactsQ.data.contacts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setContactId(c.id);
                      setContactName(c.display_name ?? `#${c.id}`);
                      setSearch('');
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-[12px] hover:bg-n-50"
                  >
                    <span>{c.display_name ?? '(sem nome)'}</span>
                    <span className="text-[11px] text-n-500">
                      {c.primary_identifier?.value ?? ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {contactId && (
              <div className="mt-2 rounded bg-brand-50 px-3 py-2 text-[12px]">
                Selecionado: <b>{contactName}</b> (ID #{contactId})
              </div>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">Tipo de pedido</span>
            <div className="space-y-2">
              {(['export', 'delete', 'anonymize'] as const).map((t) => (
                <label
                  key={t}
                  className="flex cursor-pointer items-start gap-2 rounded border border-n-200 bg-n-0 p-3 hover:bg-n-50"
                >
                  <input
                    type="radio"
                    checked={requestType === t}
                    onChange={() => setRequestType(t)}
                    className="mt-1 h-4 w-4"
                  />
                  <div className="flex-1 text-[12px]">
                    <div className="font-semibold text-n-900">{TYPE_LABELS[t]}</div>
                    <div className="mt-0.5 text-[11px] text-n-600">
                      {t === 'export' && 'Gera ZIP com tickets, mensagens e mídia. Download no painel.'}
                      {t === 'delete' && 'Remove permanentemente PII. Audit/fiscal preservado.'}
                      {t === 'anonymize' && 'Mantém o histórico mas substitui dados pessoais por hashes.'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </label>

          {(requestType === 'delete' || requestType === 'anonymize') && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              ⚠️ Esta ação é <b>irreversível</b>. Tickets e mensagens do contato serão afetados.
            </div>
          )}

          {error && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-n-200 border-t bg-n-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px]"
          >
            Cancelar
          </button>
          <Button
            onClick={() => createMut.mutate()}
            disabled={!contactId || createMut.isPending}
          >
            {createMut.isPending && <Loader2 className="animate-spin" />}
            Criar pedido
          </Button>
        </footer>
      </div>
    </div>
  );
}
